/**
 * US-65: страница установки пароля по одноразовой ссылке.
 *
 * URL: /app/set-password/<uidb64>/<token>/
 * Используется как для welcome (новый сотрудник), так и для forgot-password
 * (сброс) — backend выдаёт одинаковый token, эндпоинт один: POST
 * /api/auth/password-reset/confirm/
 *
 * После успешной установки — редирект на /app/ (страница логина).
 */

import { useEffect, useState, type FormEvent } from "react";
import {
  Alert,
  Button,
  Card,
  PasswordInput,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { AlertCircle, Check } from "lucide-react";

import { confirmPasswordReset } from "../api/client";
import { NEUTRALS } from "../theme/tokens";

const MIN_LEN = 10;
const LETTER_RE = /[A-Za-zА-Яа-яЁё]/;
const DIGIT_RE = /\d/;

function validateNewPassword(pw: string): string | null {
  if (pw.length < MIN_LEN) return `Не менее ${MIN_LEN} символов (сейчас ${pw.length}).`;
  if (!LETTER_RE.test(pw)) return "Пароль должен содержать хотя бы одну букву.";
  if (!DIGIT_RE.test(pw)) return "Пароль должен содержать хотя бы одну цифру.";
  return null;
}

interface Props {
  uidb64: string;
  token: string;
}

export function SetPasswordScreen({ uidb64, token }: Props) {
  // Экран установки пароля по ссылке из письма. Был захардкожен светлый фон —
  // у пользователя с тёмной темой (напр. после reset) выходил белый экран.
  const { colorScheme } = useMantineColorScheme();
  const neutrals = NEUTRALS[colorScheme === "dark" ? "dark" : "light"];
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  useEffect(() => {
    if (success) {
      const t = window.setTimeout(() => {
        window.location.href = "/app/";
      }, 2500);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [success]);

  const localError = validateNewPassword(newPw);
  const mismatch = newPw && confirmPw && newPw !== confirmPw;
  const canSubmit = newPw && confirmPw && !localError && !mismatch && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    setServerErrors([]);
    setSubmitting(true);
    try {
      await confirmPasswordReset(uidb64, token, newPw);
      setSuccess(true);
    } catch (e2) {
      const err = e2 as Error & { body?: { detail?: string; errors?: string[] } };
      const body = err.body;
      setServerError(body?.detail ?? err.message);
      setServerErrors(body?.errors ?? []);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: neutrals.bg,
      }}
    >
      <Card withBorder shadow="sm" padding="xl" radius="md" style={{ maxWidth: 460, width: "100%" }}>
        <Stack gap="md">
          <div>
            <Title order={2} size="h3" style={{ letterSpacing: "-0.01em" }}>
              Установка пароля
            </Title>
            <Text c="dimmed" size="sm" mt={4}>
              Задайте пароль для входа в SKEDD. Требования — не менее {MIN_LEN} символов,
              хотя бы одна буква и одна цифра.
            </Text>
          </div>

          {success ? (
            <Alert color="green" icon={<Check size={16} />} data-testid="set-pw-success">
              Пароль установлен. Сейчас перенесём на страницу входа…
            </Alert>
          ) : (
            <form onSubmit={(e) => void handleSubmit(e)}>
              <Stack gap="md">
                <PasswordInput
                  label="Новый пароль"
                  value={newPw}
                  onChange={(e) => setNewPw(e.currentTarget.value)}
                  error={newPw && localError ? localError : undefined}
                  required
                  autoFocus
                  autoComplete="new-password"
                  data-testid="set-pw-new"
                />
                <PasswordInput
                  label="Подтвердите пароль"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.currentTarget.value)}
                  error={mismatch ? "Пароли не совпадают" : undefined}
                  required
                  autoComplete="new-password"
                  data-testid="set-pw-confirm"
                />
                {serverError && (
                  <Alert color="red" icon={<AlertCircle size={16} />} data-testid="set-pw-error">
                    <Text fw={500}>{serverError}</Text>
                    {serverErrors.length > 0 && (
                      <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                        {serverErrors.map((e, i) => (
                          <li key={i}>
                            <Text size="sm">{e}</Text>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Alert>
                )}
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  loading={submitting}
                  data-testid="set-pw-submit"
                >
                  Установить пароль
                </Button>
              </Stack>
            </form>
          )}
        </Stack>
      </Card>
    </div>
  );
}

/**
 * Парсит URL /app/set-password/<uidb64>/<token>/ и возвращает токены.
 * Возвращает null если URL не подходит — родительский компонент покажет обычное App.
 */
export function parseSetPasswordUrl(pathname: string): { uidb64: string; token: string } | null {
  // Допускаем trailing slash и его отсутствие
  const m = pathname.match(/^\/app\/set-password\/([^/]+)\/([^/]+)\/?$/);
  if (!m) return null;
  return { uidb64: m[1], token: m[2] };
}
